import type { GetStaticPropsContext } from 'next';

import { Footnote } from '~/components/footnote';
import { ShapesBanner } from '~/components/shapes-banner';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { getPageTemplate } from '~/features/page-template/controllers/get-page-template';
import { FeatureComparisonTableSection } from '~/features/page-template/templates/career-outcomes/components/feature-comparison-section';
import { HeroSection } from '~/features/page-template/templates/career-outcomes/components/hero-section';
import { PartnersSection } from '~/features/page-template/templates/career-outcomes/components/partners-section';
import { ProjectsSection } from '~/features/page-template/templates/career-outcomes/components/projects-section';
import { SpotlightStatSection } from '~/features/page-template/templates/career-outcomes/components/spotlight-stat-section';
import { TestimonialsSection } from '~/features/page-template/templates/career-outcomes/components/testimonials-section';
import {
  CareerOutcomesTemplate,
  careerOutcomesTemplateQuery,
  careerOutcomesTemplatesSchema,
} from '~/features/page-template/templates/career-outcomes/model';
import { getProgram } from '~/features/program/controllers/get-program';
import { getProgramVersion } from '~/features/program/controllers/get-program-version';

export default function CareerOutcomesLandingPage({
  page,
  careerOutcomesTemplate,
  programTitles,
}: {
  page: Page;
  careerOutcomesTemplate: CareerOutcomesTemplate;
  programTitles?: Record<string, string | null>;
}) {
  const {
    pageTitle,
    pageDescription,
    graduateStats,
    testimonialsSection1Title,
    testimonialsSection1Entries,
    testimonialsSection2Title,
    testimonialsSection2Entries,
    testimonialsSection3Title,
    testimonialsSection3Entries,
    testimonialsSection4Title,
    testimonialsSection4Entries,
    spotlightStat1,
    spotlightStat2,
    projectsSectionTitle,
    projects,
    partnersSectionTitle,
    partnersSectionSubtitle,
    partnersSectionDescription,
    partners,
    featureComparisonTableSectionTitle,
    shapesBanner,
  } = careerOutcomesTemplate;
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Uncategorized} />
      <PageLayout withoutVerticalPadding>
        <HeroSection blurb={pageDescription} stats={graduateStats} title={pageTitle} />
        <TestimonialsSection
          layout='grid'
          testimonials={testimonialsSection1Entries}
          title={testimonialsSection1Title}
        />
        <SpotlightStatSection text={spotlightStat1.text} title={spotlightStat1.title} />
        <TestimonialsSection
          layout='carousel'
          testimonials={testimonialsSection2Entries}
          title={testimonialsSection2Title}
        />
        <SpotlightStatSection text={spotlightStat2.text} theme='dark' title={spotlightStat2.title} />
        <TestimonialsSection
          layout='grid'
          testimonials={testimonialsSection3Entries}
          title={testimonialsSection3Title}
        />
        <ProjectsSection programTitles={programTitles} projects={projects} title={projectsSectionTitle} />
        <TestimonialsSection
          layout='carousel'
          testimonials={testimonialsSection4Entries}
          title={testimonialsSection4Title}
        />
        <PartnersSection
          description={partnersSectionDescription}
          partners={partners}
          subtitle={partnersSectionSubtitle}
          title={partnersSectionTitle}
        />
        <FeatureComparisonTableSection title={featureComparisonTableSectionTitle} />
        <ShapesBanner
          {...shapesBanner}
          onPrimaryCtaClick={() =>
            trackEvent({
              type: 'click',
              name: 'Careers Page CTA Clicked',
              label: 'Get Started',
            })
          }
        />
        <Footnote
          text={`*https://educationdata.org/average-cost-of-a-masters-degree, **https://www.bestcolleges.com/bootcamps/payment/bootcamp-cost`}
        />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, careerOutcomesTemplate] = await Promise.all([
    getPage('/career-outcomes'),
    getPageTemplate<CareerOutcomesTemplate>({
      templateName: 'Career Outcomes Landing Page',
      templateQuery: careerOutcomesTemplateQuery,
      templateZodSchema: careerOutcomesTemplatesSchema,
    }),
  ]);

  const projectProgramKeys = careerOutcomesTemplate?.projects?.map((project) => project?.program?.key).filter(Boolean);

  const programTitles: Array<[string, string | null]> = [];

  try {
    for (const programKey of projectProgramKeys) {
      const latestProgramVersion = await getProgramVersion(programKey, 'en-us');
      if (latestProgramVersion) {
        const program = await getProgram({ key: programKey, version: latestProgramVersion });
        programTitles.push([programKey, program?.title || null]);
      } else programTitles.push([programKey, null]);
    }
  } catch (error) {
    logger.error('Error getting program titles', {
      file: 'src/pages/career-outcomes.tsx',
      function: 'getStaticProps',
      error: error,
    });
  }

  return {
    props: {
      page,
      careerOutcomesTemplate,
      programTitles: Object.fromEntries(programTitles),
    },
  };
}
